import { useState, useCallback, useEffect, useMemo, startTransition } from 'react'
import storage from 'services/localStorage'
import { isEqual } from 'lodash'
import { useDebouncedEffect } from 'hooks/debounce'

export interface IUseComplexStateOptions<T> {
  onChange: (value: T) => T
}

export type ComplexStateValueSetter<T> = (name: keyof T, value: T[typeof name]) => void
export type ComplexStateValuesSetter<T> = (values: Partial<T>) => void

export interface IUseComplexStateController<T> {
  values: Partial<T>
  replaceValues: (values: T) => void
  setValues: ComplexStateValuesSetter<T>
  setValue: ComplexStateValueSetter<T>
  clearValues: () => void
  resetDefaults: () => void
  isEmpty: boolean
}

export function useComplexState<T>(initialValues: Partial<T> = {}, options: Partial<IUseComplexStateOptions<Partial<T>>> = {}) {
  // state setup
  const [values, replaceValuesRaw] = useState<Partial<T>>(initialValues)
  const [pristine, setPristine] = useState<boolean>(true)

  // functions to handle updating values
  const replaceValues = useCallback((newValues: Partial<T>) => {
    replaceValuesRaw(newValues)
    setPristine(false)
  }, [setPristine, replaceValuesRaw])
  const setValues = useCallback((newValues: Partial<T>) => {
    replaceValues({ ...values, ...newValues })
  }, [values, replaceValues])
  const setValue = useCallback((name: keyof T, value: T[typeof name]) => {
    setValues({ [name]: value } as Partial<T>)
  }, [values, setValues])
  // other functions
  const clearValues = useCallback(() => replaceValues({}), [])
  const resetDefaults = useCallback(() => replaceValues({ ...initialValues }), [initialValues])
  // calculated values
  const isEmpty = useMemo(() => !Object.values(values).some(v => Boolean(v)), [values])
  // effects
  useEffect(() => {
    if (typeof options?.onChange === 'function') options.onChange(values)
  }, [values])

  // TODO: make this configurable?
  useEffect(() => {
    if (pristine) replaceValues(initialValues)
  }, [initialValues])

  return useMemo<IUseComplexStateController<T>>(() => ({
    values,
    replaceValues,
    setValues,
    setValue,
    clearValues,
    resetDefaults,
    isEmpty,
  }), [
    values,
    replaceValues,
    setValues,
    setValue,
    clearValues,
    resetDefaults,
    isEmpty,
  ])
}

export interface IAdditionalParameters {
  [key: string]: string | number
}

export interface IComplexStateOptions {
  /**
   * User email is required to avoid loading another user's state, especially when masking
   */
  user: { email: string | null } | null
  /**
   * Optionally pass additional parameters which will be part of the key used to save
   */
  additionalParameters?: IAdditionalParameters
  /**
   * The base name of the local storage key used to save this state
   */
  storageName: string
  /**
   * Increment this number if you changed the structure of the state, so that older versions won't be loaded
   */
  version?: number
}

// TODO: potentially move this to services?
function isSavedComplexStatePresent(options: IComplexStateOptions) {
  try {
    if (!options.user?.email) return false
    const toParse = storage.getItem(options.storageName)
    if (!toParse) return false
    const savedData = JSON.parse(toParse)
    let additionalParametersFlag = true
    if (options.additionalParameters) {
      Object.keys(options.additionalParameters).forEach((paramKey) => {
        if (options.additionalParameters?.[paramKey]) {
          additionalParametersFlag = additionalParametersFlag && savedData?.additionalParameters[paramKey] === options.additionalParameters[paramKey]
        }
      })
    }
    return additionalParametersFlag && savedData?.formatVersion === options.version && savedData.email === options.user?.email
  } catch (error) {
    console.log(error)
    return false
  }
}

// TODO: potentially move this to services?
export function loadSavedComplexState(options: IComplexStateOptions) {
  if (isSavedComplexStatePresent(options)) {
    try {
      const email = options.user?.email
      if (email) {
        const toParse = storage.getItem(options.storageName)
        if (toParse) {
          const savedData = JSON.parse(toParse)
          if (savedData?.formatVersion === options.version && savedData.email === email) {
            return savedData.value
          }
        }
      }
    } catch (error) {
      console.log(error)
    }
  }
  return null
}

/**
 * Automatically loads saved state from localStorage
 *
 * @param replaceState the hook will call this function on mount to load the saved state, if any, to local state
 * @param options for configuring the location the state will be loaded from
 */
export function useLoadComplexState<State>(replaceState: (values: State) => void, options: IComplexStateOptions) {
  useEffect(() => {
    const loadedState = loadSavedComplexState(options)
    if (loadedState) {
      replaceState(loadedState)
    }
  }, [])
}

/**
 * Automatically saves state to localStorage
 *
 * @param values the current value of the state that will be auto saved to local storage
 * @param options for configuring the storage of the persisted state
 */
export function useSaveComplexState<State>(values: State, options: IComplexStateOptions) {
  useEffect(() => {
    try {
      if (options.user?.email) {
        const email = options.user?.email
        storage.setItem(options.storageName, JSON.stringify({
          formatVersion: options.version || 0,
          value: values,
          email,
          additionalParameters: options.additionalParameters,
        }))
      }
    } catch (error) {
      console.log(error)
    }
  }, [values, options])
}

/**
 * Automatically saves and loads state from localStorage, combining useSaveComplexState and useLoadComplexState
 *
 * @param values the current value of the state, which will be auto saved to local storage
 * @param replaceValues the hook will call this function on mount to load the saved values, if any, to local state
 * @param options for configuring the storage of the persisted state
 */
export function usePersistedComplexState<State>(values: State, replaceValues: (newValues: State) => void, options: IComplexStateOptions) {
  useLoadComplexState(replaceValues, options)
  useSaveComplexState(values, options)
}

/**
 * Utility hook to handle a boolean state
 */
export function useBoolean(initialValue = false) {
  const [value, setValue] = useState<boolean>(initialValue)

  return useMemo(() => ({
    value,
    setValue,
    setTrue: () => { setValue(true) },
    setFalse: () => { setValue(false) },
    toggle: () => { setValue(!value) },
  }), [value, setValue])
}

/**
 * Debounce a value and optionally register a callback that will only fire when the value changes (deep equality)
 * @param value the value to monitor
 * @param delay the debounce delay
 * @param onChange the callback to call when the value has changed (deep equality)
*/
export function useDetectChange<T>(
  value: T,
  delay = 100,
  onChange?: (newValue: T) => void,
  initialValue?: Partial<T>,
) {
  const [lastValue, setLastValue] = useState<Partial<T>>(initialValue || value)

  useDebouncedEffect(() => {
    // TODO: possibly use JSON stringification based equality check if it's faster?
    if (!isEqual(lastValue, value)) {
      startTransition(() => {
        setLastValue(value)
        if (typeof onChange === 'function') onChange(value)
      })
    }
  }, delay, [value, lastValue, onChange], {
    leading: true,
    maxWait: undefined, // force no maxWait for this effect
  })

  return lastValue
}
